import { Injectable } from '@angular/core';
import { MenuItem } from '../../features/platform/services/ui-config-api.service';

/**
 * Menu Filter Service
 * Provides client-side filtering of menus based on user roles, permissions, and tenant plan
 * This is a fallback when backend filtering is not available or needs client-side refinement
 */
@Injectable({ providedIn: 'root' })
export class MenuFilterService {
  /**
   * Plan level hierarchy
   */
  private readonly planLevels: Record<string, number> = {
    'BASIC': 1,
    'PROFESSIONAL': 2,
    'ENTERPRISE': 3,
    'CUSTOM': 3,
  };

  /**
   * Check if tenant plan meets required plan level
   */
  checkPlanLevel(tenantPlan: string, requiredPlan?: string): boolean {
    if (!requiredPlan) return true; // No plan requirement
    
    const tenantLevel = this.planLevels[tenantPlan.toUpperCase()] || 1;
    const requiredLevel = this.planLevels[requiredPlan.toUpperCase()] || 1;
    
    return tenantLevel >= requiredLevel;
  }

  /**
   * Check if user has at least one of the required roles
   */
  hasRequiredRoles(userRoles: string[], requiredRoles?: string[]): boolean {
    if (!requiredRoles || requiredRoles.length === 0) return true; // No role requirement
    
    return requiredRoles.some(requiredRole => 
      userRoles.some(userRole => 
        userRole.toLowerCase().trim() === requiredRole.toLowerCase().trim()
      )
    );
  }

  /**
   * Check if user has all required permissions
   */
  hasRequiredPermissions(userPermissions: string[], requiredPermissions?: string[]): boolean {
    if (!requiredPermissions || requiredPermissions.length === 0) return true; // No permission requirement
    
    return requiredPermissions.every(requiredPerm => 
      userPermissions.some(userPerm => 
        userPerm.toLowerCase().trim() === requiredPerm.toLowerCase().trim()
      )
    );
  }

  /**
   * Filter menu items based on user roles, plan, and permissions
   */
  filterMenus(
    menus: MenuItem[],
    userRoles: string[],
    tenantPlan: string,
    userPermissions: string[]
  ): MenuItem[] {
    // Hide Dashboard menu for everyone, regardless of role
    let filteredMenus = menus.map(menu => {
      const labelLc = (menu.label || '').toLowerCase().trim();
      if (labelLc === 'dashboard' || labelLc === 'dashbard') {
        return null;
      }
      return menu;
    }).filter((menu): menu is MenuItem => menu !== null);

    // Platform owner sees everything (except Dashboard which was already removed)
    const isPlatformOwner = userRoles.some(r => {
      const name = r.toLowerCase().trim();
      return name === 'platform_owner' || 
             name === 'platformowner' ||
             name === 'super_admin' || 
             name === 'superadmin';
    });
    
    if (isPlatformOwner) {
      return filteredMenus;
    }

    return filteredMenus
      .map(menu => {
        // Create a copy to avoid mutating the original
        const filteredMenu = { ...menu };
        
        // Recursively filter children first
        if (filteredMenu.children && filteredMenu.children.length > 0) {
          filteredMenu.children = this.filterMenus(
            filteredMenu.children,
            userRoles,
            tenantPlan,
            userPermissions
          );
        }
        
        // Check if menu is hidden for specific roles
        if (filteredMenu.hiddenForRoles && Array.isArray(filteredMenu.hiddenForRoles)) {
          const isHidden = filteredMenu.hiddenForRoles.some(hiddenRole => 
            userRoles.some(userRole => 
              userRole.toLowerCase().trim() === hiddenRole.toLowerCase().trim()
            )
          );
          
          if (isHidden) {
            // If it has visible children, keep it but mark as no route
            if (filteredMenu.children && filteredMenu.children.length > 0) {
              return { ...filteredMenu, route: undefined };
            }
            return null;
          }
        }
        
        // NEW: Check requiredPlan (plan level requirement)
        if (filteredMenu.requiredPlan) {
          if (!this.checkPlanLevel(tenantPlan, filteredMenu.requiredPlan)) {
            // If it has visible children, keep it but mark as no route
            if (filteredMenu.children && filteredMenu.children.length > 0) {
              return { ...filteredMenu, route: undefined };
            }
            return null;
          }
        }
        
        // NEW: Check requiredRoles array (takes precedence over single role)
        if (filteredMenu.requiredRoles && Array.isArray(filteredMenu.requiredRoles)) {
          if (!this.hasRequiredRoles(userRoles, filteredMenu.requiredRoles)) {
            // If it has visible children, keep it but mark as no route
            if (filteredMenu.children && filteredMenu.children.length > 0) {
              return { ...filteredMenu, route: undefined };
            }
            return null;
          }
          // If user has required roles, show the menu
          return filteredMenu;
        }
        
        // LEGACY: Check if menu requires a specific role (single role - for backward compatibility)
        if (filteredMenu.role) {
          const requiredRole = filteredMenu.role.toLowerCase().trim();
          const hasRole = userRoles.some(userRole => 
            userRole.toLowerCase().trim() === requiredRole
          );
          
          if (!hasRole) {
            // If it has visible children, keep it but mark as no route
            if (filteredMenu.children && filteredMenu.children.length > 0) {
              return { ...filteredMenu, route: undefined };
            }
            return null;
          }
          // If user has the required role, show the menu
          return filteredMenu;
        }
        
        // NEW: Check requiredPermissions array (all permissions must be present)
        if (filteredMenu.requiredPermissions && Array.isArray(filteredMenu.requiredPermissions)) {
          if (!this.hasRequiredPermissions(userPermissions, filteredMenu.requiredPermissions)) {
            // If it has visible children, keep it but mark as no route
            if (filteredMenu.children && filteredMenu.children.length > 0) {
              return { ...filteredMenu, route: undefined };
            }
            return null;
          }
          return filteredMenu;
        }
        
        // LEGACY: Check if menu requires a specific permission (single permission - for backward compatibility)
        if (filteredMenu.permission) {
          const requiredPermission = filteredMenu.permission.toLowerCase();
          const hasPermission = userPermissions.some(userPerm => 
            userPerm.toLowerCase() === requiredPermission
          );
          
          if (!hasPermission) {
            // If it has visible children, keep it but mark as no route
            if (filteredMenu.children && filteredMenu.children.length > 0) {
              return { ...filteredMenu, route: undefined };
            }
            return null;
          }
        }
        
        return filteredMenu;
      })
      .filter((menu): menu is MenuItem => menu !== null);
  }
}